import {debounce} from '@github/mini-throttle'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {announce} from '@github-ui/aria-live'
import {newRepoPath, orgReposListPath} from '@github-ui/paths'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ReposFilter} from '@github-ui/repos-filter'
import {
  addSortToQuery,
  defaultSortingOptions,
  getScreenReaderMessage,
  getSortFromQuery,
  ReposList,
  useCompactModeSetting,
} from '@github-ui/repos-list'
import type {UserInfo} from '@github-ui/repos-list-blankslate'
import {getNoRepoMessage, NoReposMessage} from '@github-ui/repos-list-blankslate'
import {useSearchParams} from '@github-ui/use-navigate'
import type {ListResults, RepositoryItem} from '@github-ui/use-repos-list-results'
import {useListResults} from '@github-ui/use-repos-list-results'
import {Button, PageLayout, Pagination} from '@primer/react'
import {useEffect, useRef, useState} from 'react'

import {getTypeFromQuery, OrgSidebar, OrgSidebarCollapsedButton} from '../components/OrgSidebar'
import type {PagePayload} from '../types'
import {orgReposPageRoute} from './org-repos-page-route'
import styles from './OrgReposPage.module.css'

const DEFAULT_SORTING_KEY = 'updated'

const debounceAnnounce = debounce(announce, 300)

export function OrgReposPageForDataRouter() {
  const {data} = useRouteQuery(orgReposPageRoute, 'mainQuery')
  const {org} = useRouteParams(orgReposPageRoute)

  return (
    <AnalyticsProvider appName="org-repos-page" category="Org Repos page" metadata={{page: 'org-repos'}}>
      <OrgReposPage payload={data} org={org} />
    </AnalyticsProvider>
  )
}

interface OrgReposPageProps {
  payload: PagePayload
  org?: string
}

export function OrgReposPage({payload, org}: OrgReposPageProps) {
  const [params] = useSearchParams()

  const [fetchedQuery, setFetchedQuery] = useState(params.get('q') || '')
  const [query, setQuery] = useState(fetchedQuery)

  const {userInfo, definitions, typeFilters, searchable, compactMode} = payload
  const [fetchedType, setFetchedType] = useState(() => getTypeFromQuery(query, typeFilters))

  const fetchUrl = orgReposListPath({org: org || ''})
  const {results, currentPage, isFetching, fetchResults} = useListResults(payload, fetchUrl, Number(params.get('page')))
  const {repositories, repositoryCount, pageCount} = results

  const message = getOrgReposScreenReaderMessage(isFetching, currentPage, fetchedQuery, results, userInfo)
  useEffect(() => {
    if (message) debounceAnnounce(message)
  }, [message])

  const {canCreateRepository} = userInfo || {}

  const showFilterDialog = useFeatureFlag('repos_list_show_filter_dialog')

  function updateQuery(q: string) {
    setQuery(q)
  }

  async function submitQuery(q: string) {
    await fetchResults({q, page: 1})
    setFetchedType(getTypeFromQuery(q, typeFilters))
    setFetchedQuery(q)
  }

  function updateAndSubmitQuery(q: string) {
    updateQuery(q)
    submitQuery(q)
  }

  const {updateCompactModeSetting} = useCompactModeSetting(compactMode, 'orgs_repos_compact_mode')

  const listTitle = typeFilters.find(({id}) => id === fetchedType)?.text || 'All'

  const reposListRef = useRef<HTMLUListElement>(undefined)

  return (
    <PageLayout containerWidth="full" columnGap="none" padding="none">
      <PageLayout.Pane
        divider="line"
        hidden={{
          narrow: true,
          regular: false,
          wide: false,
        }}
        padding="condensed"
        position="start"
      >
        <aside>
          <OrgSidebar type={fetchedType} onQueryChanged={updateAndSubmitQuery} types={typeFilters} />
        </aside>
      </PageLayout.Pane>
      {/* setting px on top of the padding prop is required to visually compensate padding of the list items in the sidebar */}
      <PageLayout.Content padding="condensed" className={styles.orgReposPageContent}>
        {searchable && (
          <>
            <div className={styles.pageHeaderContainer}>
              <div className={styles.mobileNavContainer}>
                <OrgSidebarCollapsedButton
                  type={fetchedType}
                  onQueryChanged={updateAndSubmitQuery}
                  types={typeFilters}
                />
              </div>
              <h1 className={styles.pageTitle}>{listTitle}</h1>
              <div className={styles.actionButtonsContainer}>
                {canCreateRepository && (
                  <Button as="a" href={newRepoPath({org})} variant="primary" size="medium">
                    New repository
                  </Button>
                )}
              </div>
            </div>
            <ReposFilter
              id="repos-list-filter"
              label="Search repositories"
              variant={showFilterDialog ? 'full' : 'input'}
              placeholder="Search repositories"
              filterValue={query}
              definitions={definitions}
              onChange={updateQuery}
              onSubmit={filterQuery => submitQuery(filterQuery.raw)}
            />
          </>
        )}

        <div className={styles.repositoriesListWrapper}>
          <div data-hpc>
            <ReposList
              listRef={reposListRef}
              showNameWithOwner={false}
              compactMode={compactMode}
              onCompactModeChanged={updateCompactModeSetting}
              showSpinner={isFetching}
              repositoryCount={repositoryCount}
              repos={repositories}
              onSortingItemSelect={sort => updateAndSubmitQuery(addSortToQuery(fetchedQuery, sort))}
              sortingItemSelected={getSortFromQuery(query)}
              defaultSortingKey={DEFAULT_SORTING_KEY}
              sortingOptions={defaultSortingOptions}
              noReposMessage={
                <NoReposMessage
                  currentPage={currentPage}
                  filtered={!!fetchedQuery}
                  orgName={org}
                  pageCount={pageCount}
                  userInfo={userInfo}
                />
              }
            />
          </div>
        </div>
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            currentPage={currentPage}
            onPageChange={async (e, page) => {
              // Avoid adding hash parameters
              e.preventDefault()

              await fetchResults({q: fetchedQuery, page})
              reposListRef.current?.focus()
              window.scrollTo({top: 0})
            }}
          />
        )}
      </PageLayout.Content>
    </PageLayout>
  )
}

function getOrgReposScreenReaderMessage(
  isFetching: boolean,
  currentPage: number,
  fetchedQuery: string,
  results: ListResults<RepositoryItem>,
  userInfo: UserInfo | undefined,
) {
  return getScreenReaderMessage(
    defaultSortingOptions,
    DEFAULT_SORTING_KEY,
    getNoRepoMessage,
    isFetching,
    currentPage,
    fetchedQuery,
    results,
    userInfo,
  )
}

try{ OrgReposPageForDataRouter.displayName ||= 'OrgReposPageForDataRouter' } catch {}
try{ OrgReposPage.displayName ||= 'OrgReposPage' } catch {}